import { MouseEvent, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Box } from 'components/Box/Box';
import { ButtonOrLink } from 'components/ButtonOrLink/ButtonOrLink';
import { Icon } from 'components/Icon/Icon';
import { Text } from 'components/Text/Text';
import { colors, fontSizes, fontWeights } from 'theme/theme';
import { MappedLocalNotification } from 'types/LocalNotification/MappedLocalNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { shortDateTime } from 'utils/date';

import { NotificationListItemCloseButton } from './NotificationListItem.styled';
import { NotificationListItemContent } from './NotificationListItemContent';

const UnreadNotificationBadge = styled.div`
  width: 4px;
  height: 4px;
  padding: 4px;
  border-radius: 50%;
  background-color: ${colors.brandYellow};
`;

const StyledButtonOrLink = styled(ButtonOrLink)`
  display: flex;
  padding: 14px;
  padding-right: 30px;
  color: ${colors.mediumContentGrey};
  font-weight: ${fontWeights.normal};
  text-decoration: none;
  border-bottom: 1px solid ${colors.selectionGrey};

  &:hover {
    color: ${colors.mediumContentGrey};
    text-decoration: none;
    background-color: ${colors.inputGrey};
  }

  &:active {
    background-color: ${colors.softBlue};
    text-decoration: none;
  }
`;

type Props = {
  notification: ApiNotification | MappedNotification | MappedLocalNotification;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onDismiss?: (
    notification:
      | ApiNotification
      | MappedNotification
      | MappedLocalNotification,
  ) => void;
  qaId?: string;
};

export function NotificationListItem({
  notification,
  qaId,
  onClick,
  onDismiss,
}: Props) {
  const handleClick = useCallback(
    (data: {
      event: MouseEvent<HTMLElement>;
      eventName: string;
      eventProperties: Record<string, unknown>;
    }) => {
      const { event, eventProperties, eventName } = data;
      trackClicked(eventName, eventProperties);
      if (onClick) onClick(event);
    },
    [onClick],
  );

  const qaRead = useMemo(() => {
    if (!('read' in notification)) return;
    return notification.read ? 'true' : 'false';
  }, [notification]);

  return (
    <Box
      data-qa-id={qaId}
      data-qa-read={qaRead}
      position="relative"
      minWidth="260px"
    >
      <NotificationListItemContent
        notification={notification}
        render={({ to, eventName, eventProperties, className, children }) => (
          <div style={{ position: 'relative' }}>
            <StyledButtonOrLink
              type="link"
              className={className}
              href={to}
              onClick={(event) =>
                handleClick({ event, eventName, eventProperties })
              }
            >
              <div style={{ paddingTop: 6, width: 20, flex: '0 0 auto' }}>
                {'read' in notification && !notification.read ? (
                  <UnreadNotificationBadge />
                ) : null}
              </div>

              <div style={{ paddingRight: 30 }}>
                {children}

                {'created' in notification && notification.created && (
                  <Text color={colors.brandBlue} fontSize={fontSizes[12]}>
                    {shortDateTime(notification.created)}
                  </Text>
                )}
              </div>
            </StyledButtonOrLink>

            {onDismiss && (
              <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                <NotificationListItemCloseButton
                  data-qa-id="dismiss-notification"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackEvent('Dismissed: User Menu Notifications', {
                      notification_type:
                        notification.type === 'CONFIGURED_NOTIFICATION'
                          ? notification.data.details.analyticsName
                          : notification.type,
                    });
                    onDismiss(notification);
                  }}
                >
                  <Icon name="close" size={12} />
                </NotificationListItemCloseButton>
              </div>
            )}
          </div>
        )}
      />
    </Box>
  );
}
